<template>
  <layout :drawer="false">

    <template slot="nav">
      <v-tabs>
        <v-tab @click="backToNLQList">Natural Language Query</v-tab>
      </v-tabs>
    </template>

    <template slot="nav-tools">
      <smart-btn small v-show="!isNewPrompt" @click.native="onNewNLQ" class="mr-2">
        <v-icon>mdi-new-box</v-icon>
      </smart-btn>
      <smart-btn small @click.native="backToNLQList" class="mr-4">
        <v-icon>mdi-file-table-box-outline</v-icon>
      </smart-btn>
    </template>

    <template slot="content">
        <v-container fluid  class="main-container pa-8">
              <v-row align-content="start">
                <!--[1] PROMPT-BLOCK -->
                <v-col cols="12" >
                  <v-card outlined rounded="lg" class="nlq-card-first px-8 py-4">
                    <v-card outlined rounded="lg" class="nlq-inner-card px-4 py-0" >
                      <v-textarea
                          v-model="promptDraft"
                          class="prompt-textarea teal--text text--lighten-1"
                          :class="{ 'prompt-textarea--locked': !isUpdatingPrompt && !isNewPrompt }"
                          placeholder="Prompt..."
                          ghost
                          color="teal"
                          rows="2"
                          auto-grow
                          hide-details="auto"
                          @keydown.ctrl.enter="sendPrompt"
                      />
                    </v-card>

                    <v-card-actions class="justify-space-between pa-0 mt-4">
                      <!-- Left -->
                      <div class="d-flex gap3 align-center">
                        <p class="nlq-label">{{ promptStatus }}</p>
                        <div class="d-flex info-chip-wrapper">
                            <v-chip outlined v-for="item in llmBenchmarkItems" :key="item.label" class="info-chip py-0" color="blue-grey lighten-4">
                              <div class="d-flex align-baseline gap1">
                                <p class="caption-xs nlq-label">
                                  {{ item.label }}
                                </p>
                                <span class="nlq-text caption">
                                  {{ item.value }}
                                </span>
                              </div>
                            </v-chip>
                        </div>
                        <NlQueryStatusChip v-if="nlQuery" x-small :value="nlQuery.status" />
                      </div>
                      <!-- Right -->
                      <div class="d-flex gap1">
                        <v-icon v-if="error">error</v-icon>
                        <v-skeleton-loader v-if="isSending" />
                        <smart-btn v-if="isNewPrompt || isUpdatingPrompt" primary class="teal darken-1"
                            :disabled="!canSendPrompt"
                            :loading="isSending"
                            @click.native="sendPrompt">
                          <span>Générer la requête</span>
                        </smart-btn>
                      </div>
                    </v-card-actions>
                  </v-card>
                </v-col>

                <!-- [2] SQL -->
                <v-col cols="6" >
                  <v-card outlined rounded="lg" class="nlq-card px-8 py-4">
                    <div class="mb-4 d-flex justify-space-between">
                      <p class="nlq-label monospace">REQUÊTE GÉNÉRÉE</p>
                      <div class="nlq-title-row">
                        <v-chip small class="clipboard-chip-absolute clipboard-chip cursor-pointer" @click="clipboardSql">
                          <v-icon small>{{ isCopied ? "mdi-clipboard" : "mdi-clipboard-outline" }}</v-icon>
                        </v-chip>
                      </div>
                    </div>
                    <v-card outlined rounded="lg" class="nlq-inner-card px-4 py-2" style="min-height: 100px;">
                      <!--[!] Garder sans saut de lignes-->
                      <pre class="sql-code line-numbers"><code class="language-sql"
                          v-html="highlightSql"
                      ></code></pre>
                    </v-card>
                  </v-card>
                </v-col>


                <!-- [3] Explications + Révision -->
                <v-col cols="6" >
                  <v-card outlined rounded="lg" class="nlq-card px-8 py-4">
                    <div class="mb-4">
                      <p class="nlq-label monospace">EXPLICATIONS</p>
                    </div>

                    <p class="nlq-text">{{explanation}}</p>

                    <div class="mt-4" v-if="canUpdateStatus">
                      <template v-if="status === NLQ_STATUS.REJECTED" >
                        <v-divider class="teal lighten-5 mb-4"/>
                        <div class="mb-4">
                          <p class="nlq-label monospace">CAUSE DU REJET</p>
                        </div>
                        <p class="nlq-text mb-4">{{ nlQuery.rejectionCause }}</p>
                      </template>

                      <v-divider class="teal lighten-5 mb-4"/>
                      <div class="mb-4">
                        <p class="nlq-label monospace">RÉVISION</p>
                      </div>

                      <div class="d-flex gap1 align-center">
                        <p class="caption nlq-label mr-4">Modifier le statut en </p>
                        <NlQueryStatusChip x-small class="cursor-pointer" v-for="item in availableStatus" :key="`chip-update-${item}`"
                                           :value="item" v-show="item !== status" :no-color="item !== movingStatus"
                                           @click.native="updatingStatusPressed(item)" />
                        <v-icon small  v-if="movingStatus" @click.native="resetMovingStatus()" class="cursor-pointer ml-4">cancel</v-icon>
                        <v-icon small  v-if="movingStatus" @click.native="validateMovingStatus()" class="cursor-pointer">check</v-icon>
                      </div>
                      <v-card outlined rounded="lg" class="nlq-inner-card px-4 py-0 mt-2"  v-if="showRejectionInput" >
                        <v-textarea
                            v-model="rejectionCauseDraft"
                            class="prompt-textarea-sm blue-grey--text text--lighten-1"
                            placeholder="Rejeté car ..."
                            ghost
                            color="blue-grey"
                            rows="1"
                            auto-grow
                            hide-details="auto"
                            @keydown.ctrl.enter="validateMovingStatus()"
                        />
                      </v-card>
                    </div>
                    <div class="mt-1">
                      <p class=" caption red--text">{{movingStatusError}}</p>
                    </div>
                  </v-card>
                </v-col>


                <v-col cols="12" >
                  <v-card outlined rounded="lg" class="nlq-card px-8 py-4">
                    <v-card-actions class="justify-space-between pa-0 mb-2">
                      <!-- Left -->
                      <div class="d-flex gap1 align-center">
                        <p class="nlq-label">Vérifier une requête avant de l'exécuter</p>
                        <v-chip v-if="executionRows.length > 0" small class="ml-4 cursor-pointer clipboard-chip" @click="exportCsv('executionResultTable')">
                          <v-icon small>mdi-file-download</v-icon>
                        </v-chip>

                      </div>
                      <!-- Right -->
                      <div class="d-flex gap1" v-if="canExecute">
                        <v-icon v-if="error">error</v-icon>
                        <v-skeleton-loader v-if="isExecuting" />
                        <smart-btn primary class="teal darken-1" :disabled="!canExecute" :loading="isExecuting" @click.native="executeSql">
                          <span>Exécuter la requête</span>
                        </smart-btn>
                      </div>
                    </v-card-actions>
                    <v-card outlined rounded="lg" class="nlq-inner-card pa-0" style="min-height: 200px;" >
                      <v-data-table
                          v-if="executionRows.length > 0"
                          :headers="executionHeaders"
                          :items="executionRows"
                          :items-per-page.sync="executionItemsPerPage"
                          :footer-props="{
                            'items-per-page-options': executionItemsPerPageOptions,
                            'items-per-page-text': 'Lignes par page :',
                            'items-per-page-all-text': 'Toutes'
                          }"
                          dense
                          class="execution-table"
                          ref="executionResultTable"
                      />

                      <div v-else-if="executionResult !== null" class="pa-4 nlq-text">
                        La requête n'a retourné aucune ligne.
                      </div>
                    </v-card>
                  </v-card>
                </v-col>


                <!-- [X] DEBUG -->
                <v-col cols="12" v-if="false">
                  <v-card outlined rounded="lg" class="nlq-card py-2 px-8">
                    <div class="caption" style="font-size: 7px;">
                      <div class="d-flex justify-space-between">
                        <p>ISNEW : <span class="blue--text text--darken-2">{{isNew}}</span></p>
                        <p>ID : <span class="blue--text text--darken-2">{{id}}</span></p>
                        <p>ISNEWPROMPT : <span class="blue--text text--darken-2">{{isNewPrompt}}</span></p>
                        <p>canSendPrompt :<span class="blue--text text--darken-2">{{canSendPrompt}}</span></p>
                        <p>isSending : <span class="blue--text text--darken-2">{{isSending}}</span></p>
                        <p>isUpdatingPrompt : <span class="blue--text text--darken-2">{{isUpdatingPrompt}}</span></p>
                        <p>movingStatus : <span class="pink--text text--darken-2">{{movingStatus}}</span></p>

                        <p>error : <span class="blue text--darken-2">{{error}}</span></p>
                      </div>
                      <p class="mt-2 monospace blue-grey--text">nlQuery : {{ nlQuery }}</p>
                    </div>
                  </v-card>
                </v-col>

              </v-row>
            </v-container>
    </template>
  </layout>
</template>

<script>
import Layout from "@/views/Layout.vue";
import {get, post} from "@/utils/api";
import NlQueryStatusChip from "@/components/commons/NlQueryStatusChip.vue";
import { formatDialect, postgresql } from "sql-formatter"
import Prism from "prismjs"
import "prismjs/components/prism-sql"
import "prismjs/themes/prism.css"
import "prismjs/plugins/line-numbers/prism-line-numbers"
import "prismjs/plugins/line-numbers/prism-line-numbers.css"
import ExportMixin from "@/components/mixins/ExportMixin";
import { NLQ_STATUS } from "@/components/commons/NlQueryStatusChip.vue";




export default {
  name: "NaturalLanguageQueryDetailView",
  components: {
    NlQueryStatusChip,
    Layout
  },
  mixins: [ExportMixin],
  props: {
    isNew: { type:Boolean, default:false },
    id: {type: String, required: true}
  },
  data: () => ({
    promptDraft: "",

    isCreationMode: true,
    currentId: null,

    isSending: false,
    isLoading: false,
    isExecuting: false,
    error: null,

    movingStatus: null,
    movingStatusError: null,
    rejectionCauseDraft: "",

    isCopied: false,
    copyTimeout: null,

    nlQuery: null,
    executionResult: null,
    executionItemsPerPage: 20,
  }),
  created() {
    this.currentId = this.id
    this.isCreationMode = this.isNew || this.id === "0"
    this.initializeView()
  },
  mounted() {

  },
  beforeDestroy() {
    if (this.copyTimeout) { clearTimeout(this.copyTimeout) }
  },

  computed: {
    isNewPrompt() {
      return this.isCreationMode
    },
    isUpdatingPrompt() {
      if (!this.nlQuery || this.isNewPrompt) { return false }

      return this.promptDraft !== this.nlQuery.prompt
    },
    canSendPrompt() {
      return (this.isNewPrompt || this.isUpdatingPrompt) && this.promptDraft.trim().length > 0 && !this.isLoading && !this.isSending
    },
    canExecute() {
      return this.nlQuery !== null && [NLQ_STATUS.REVIEW, NLQ_STATUS.VALIDATED].includes(this.nlQuery.status)
    },
    generatedSql() {
      return this.nlQuery ? this.nlQuery.sql || "" : ""
    },
    explanation() {
      return this.nlQuery ? this.nlQuery.explanation || "" : ""
    },
    canUpdateStatus() {
      return this.nlQuery && this.nlQuery.status !== NLQ_STATUS.FAILED
    },
    availableStatus() {
      return Object.values(NLQ_STATUS).filter(s => s !== NLQ_STATUS.FAILED)
    },
    NLQ_STATUS() {
      return NLQ_STATUS
    },
    status() {
      if (!this.nlQuery) { return null }
      return this.nlQuery.status
    },
    showRejectionInput() {
      return this.movingStatus === NLQ_STATUS.REJECTED
    },
    promptStatus() {
      if (this.isLoading) {
        return "Chargement de la requête...";
      }
      if (this.error) {
        return `Erreur : ${this.error}`;
      }
      if (this.isSending) {
        return "Génération de la requête en cours...";
      }
      if (!this.isNewPrompt) {
        return "Requête archivée";
      }
      if (!this.canSendPrompt) {
        return "Créez votre prompt";
      }
      if (this.nlQuery) {
        return "Requête générée";
      }
      return "Création du prompt";
    },
    formattedSql() {
      if (!this.generatedSql) { return "" }

      try {
        return formatDialect(this.generatedSql, {
          dialect: postgresql,
          tabWidth: 2,
          keywordCase: "upper",
          // indentStyle: "tabularLeft",
          linesBetweenQueries: 1
        })
      } catch (error) {
        console.error("[NQLDetail] SQL formatting error:", error)
        return this.generatedSql
      }
    },
    highlightSql(){
      return Prism.highlight(this.formattedSql, Prism.languages.sql, 'sql');
    },
    llmBenchmarkItems() {
      if (!this.nlQuery) { return [] }

      return [
        {
          label: "Fournisseur",
          value: this.nlQuery.llmProvider,
        },
        {
          label: "Modèle",
          value: this.nlQuery.llmModel,
        },
        {
          label: "Temps",
          value: `${this.nlQuery.llmLatency} ms`,
        },
        {
          label: "Coût",
          value: `${this.nlQuery.llmCost} €`,
        },
      ]
    },
    executionRows() {
      return Array.isArray(this.executionResult) ? this.executionResult : []
    },
    executionHeaders() {
      if (this.executionRows.length === 0) { return [] }
      return Object.keys(this.executionRows[0]).map(columnName => ({
        text: columnName,
        value: columnName,
        sortable: true,
      }))
    },
    executionItemsPerPageOptions() {
      const rowCount = this.executionRows.length
      if (rowCount === 0) { return [] }

      const standardOptions = [10, 20, 50, 100, 200].filter(value => value < rowCount)

      return [...standardOptions, -1,] // -1 == All
    },
  },
  methods: {
    initializeView() {
      if (this.isNewPrompt) {
        this.initializeNewQuery();
      } else {
        this.fetchNLQuery();
      }
    },
    initializeNewQuery() {
      this.promptDraft = ""
      this.nlQuery = null
      this.error = null
      this.executionResult = null
      this.resetMovingStatus()
    },
    fetchNLQuery() {
      if (this.isNewPrompt) { return }
      this.isLoading = true
      this.error = null

      const url = `/api/db/query/${this.id}`

      get(url)
          .notFound(() => {
            this.error = "La requête demandée n'existe pas."
            this.isLoading = false
          })
          .unauthorized(() => {
            this.error = "Permission requise."
            this.isLoading = false
          })
          .badRequest(() => {
            this.error = "Impossible de charger la requête."
            this.isLoading = false
          })
          .json((r) => {
            this.nlQuery = r || null
            this.promptDraft = r ? r.prompt || "" : ""
            this.executionResult = null
            this.isLoading = false
          });
    },
    updatePrompt() {
      if (!this.promptDraft.trim() || this.isSending || this.isNewPrompt || !this.nlQuery) { return }
      this.isSending = true
      this.executionResult = null

      const url = `/api/db/query/build/${this.nlQuery.id}`
      const payload = {prompt: this.promptDraft}

      console.log("[updatePrompt] call with url:", url)

      post(url, payload)
          .badRequest(() => {
            this.error = true
            this.isSending = false
          })
          .json((r) => {
            this.nlQuery = r
            this.isSending = false
          });
    },
    sendPrompt() {
      if (!this.promptDraft.trim() || this.isSending) { return }
      if (!this.isNewPrompt) {
        this.updatePrompt()
        return
      }
      this.isSending = true

      const url = "/api/db/query/build"
      const payload = {prompt: this.promptDraft}
      console.log("[sendPrompt] call with url:", url)

      post(url, payload)
        .badRequest(() => {
          this.error = true
          this.isSending = false
        })
        .json((r) => {
          this.nlQuery = r
          this.currentId = String(r.id)
          this.executionResult = null

          this.isCreationMode = false
          this.isSending = false
          this.$router.replace({
            name: "NaturalLanguageQueryDetail",
            params: {
              id: this.currentId
            }
          })

        });
    },
    executeSql() {
      if (!this.canExecute) { return }
      this.isExecuting = true
      this.error = null

      const url = `/api/db/query/${this.id}/exec`

      get(url)
          .notFound(() => {
            this.error = "La requête à exécuter n'existe pas."
            this.isExecuting = false
          })
          .unauthorized(() => {
            this.error = "Permission requise."
            this.isExecuting = false
          })
          .badRequest(() => {
            this.error = "Impossible d'exécuter la requête."
            this.isExecuting = false
          })
          .json((r) => {
            this.executionResult = r || null
            this.initializeExecutionPagination()
            this.isExecuting = false
          });

    },
    resetMovingStatus() {
      this.movingStatus = null
      this.movingStatusError = null
      this.rejectionCauseDraft = ""
    },
    updatingStatusPressed(item) {
      if (!this.availableStatus.includes(item)) { return }
      this.movingStatus = item
    },
    validateMovingStatus() {
      if (this.isNewPrompt || !this.canUpdateStatus) { return }
      if (this.movingStatus === NLQ_STATUS.REJECTED && this.rejectionCauseDraft.trim().length === 0) {
        this.movingStatusError = "Raison du rejet requise"
        return
      }

      const url = `/api/db/query/${this.nlQuery.id}/update-status`
      const payload = {status:this.movingStatus, rejectionCause: this.rejectionCauseDraft.trim()}

      post(url, payload)
          .badRequest(() => {
            this.movingStatusError = 'Erreure lors de la mise à jour du status.'
          })
          .json((r) => {
            this.nlQuery = r
            this.executionResult = null
            this.resetMovingStatus()
          });
    },
    initializeExecutionPagination() {
      const rowCount = this.executionRows.length

      if (rowCount <= 10) {
        this.executionItemsPerPage = -1
      } else if (rowCount < 20) {
        this.executionItemsPerPage = 10
      } else {
        this.executionItemsPerPage = 20
      }
    },
    async clipboardSql() {
      if (!this.generatedSql) { return }

      try {
        await navigator.clipboard.writeText(this.generatedSql)
        this.isCopied = true
        if (this.copyTimeout) { clearTimeout(this.copyTimeout) }

        this.copyTimeout = setTimeout(() => {
          this.isCopied = false
          this.copyTimeout = null
        }, 5000)
      } catch (error) {
        console.error("[NaturalLanguageQueryDetailView] Clipboard error:", error)
      }
    },

    onNewNLQ() {
      this.$router.push({
        name: "NaturalLanguageQueryDetail",
        params: {
          id: "0",
          isNew: true
        }
      })
    },
    backToNLQList() {
      this.$router.push({
        name: "NaturalLanguageQuery",
      })
    }

  }// [N] end methods
}
</script>

<style scoped>
.main-container {
  height: calc(100vh - 96px);
  display: flex;
  flex-direction: column;
  overflow: auto;
  background-color: #ECEFF1; /* blue-grey lighten-5 */
}

.nlq-card-first, .nlq-card  {
  border-color: #CFD8DC; /* blue-grey lighten-4 */
}
.nlq-card {
  min-height: 180px;
}

.nlq-inner-card {
  background-color: #ECEFF1; /* blue-grey lighten-5 */
  border-color: #CFD8DC; /* blue-grey lighten-4 */
}

.nlq-title-row {
  position: relative;
  padding-right: 36px;
}

.nlq-label {
  color: #90A4AE; /* blue-grey lighten-2 */
  /* color: #B0BEC5; */ /* blue-grey lighten-3 */
  font-weight: 450;
}

.nlq-text {
  /* color: #78909C; */ /* blue-grey lighten-1 */
  color: #607D8B; /* blue-grey */
}

.clipboard-chip-absolute {
  position: absolute;
  top: 0;
  right: 0;
}

.clipboard-chip {
  width: 32px;
  height: 22px;
  padding: 0 !important;

  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cursor-pointer {
  cursor: pointer;
  user-select: none;
}

.monospace {
  font-family: monospace;
}

.prompt-textarea::v-deep .v-input__slot::before,
.prompt-textarea::v-deep .v-input__slot::after,
.prompt-textarea-sm::v-deep .v-input__slot::before,
.prompt-textarea-sm::v-deep .v-input__slot::after {
  display: none !important;
}

.prompt-textarea::v-deep textarea {
  color: #009688 !important; /* teal */
  -webkit-text-fill-color: #009688 !important;
}
.prompt-textarea-sm::v-deep textarea {
  font-size: 12px !important;
  color: #455A64 !important; /* teal */
  -webkit-text-fill-color: #455A64 !important;
  padding: 0px 0 !important;
}

.prompt-textarea-sm.v-text-field {
  padding-top: 0 !important;
}

.prompt-textarea-sm::v-deep textarea::v-deep  {
  font-size: 12px !important;
  color: #455A64 !important; /* teal */
  -webkit-text-fill-color: #455A64 !important;
}


::v-deep .prompt-textarea--locked textarea {
  color: #00897B !important; /* teal darken-2 */
  -webkit-text-fill-color: #00897B !important;
  font-weight: 450 !important;
  opacity: 1;
}

.test {
  border: 1px solid #00ff00;
}
.gap1 {
  gap:4px;
}

.gap3 {
  gap:12px;
}

.sql-code {
  margin: 0;
  overflow-x: auto;

  font-family: Monaco, Menlo, Consolas, monospace;
  font-size: 13px;
  line-height: 1.6;

  white-space: pre;
}

.sql-code code {
  font-family: inherit;
  display: block;
  padding: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}

::v-deep .sql-code {
  margin: 0 !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;

  background: transparent !important;
  text-shadow: none !important;
}

::v-deep .sql-code code {
  display: block;
  margin: 0 !important;
  padding: 0 !important;

  background: transparent !important;
  box-shadow: none !important;
  text-shadow: none !important;
}

::v-deep .sql-code .token {
  background: transparent !important;
  box-shadow: none !important;
  text-shadow: none !important;
}

::v-deep .sql-code.line-numbers {
  padding-left: 4em !important;
}

::v-deep .sql-code .line-numbers-rows {
  border-right: 1px solid #CFD8DC; /* blue-grey lighten-4 */
}

::v-deep .sql-code .line-numbers-rows > span::before {
  color: #90A4AE;  /* blue-grey lighten-2 */
}

.info-chip {
  border-radius: 4px;
  cursor: pointer;
  height: 22px;
}

.info-chip-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  user-select: none;
}

.caption-xs {
  font-size: 0.60rem;
  font-weight: 400;
  letter-spacing: 0.030em;
  line-height: 1.10rem;
}


</style>